<script setup>
import store from '@/store'
import { ref, computed, watch } from 'vue'
import { cloneDeep, isNil, isEmpty, isEqual, omit } from 'lodash-es'
import { defaultVTooltipProps } from '@/utils/defaultProps.js'
import NestedCategorySelect from '@/components/categorized-nested-select/NestedCategorySelect.vue'
import PeriodicTable from '@/components/periodic-table/PeriodicTable.vue'
import ClearableSelect from '@/components/ui/ClearableSelect.vue'
import MolecularFormulaManualCard from './MolecularFormulaManualCard.vue'
import {
  defaultSearchOptions,
  authorities,
  toxicities,
  massType,
  similarityTypes,
  allSearchTypes,
} from '@/components/search/options'
import { hasInvalidElementSymbol } from '@/components/periodic-table/periodicTableGrid'
import { isStringOverlapping } from '@/components/periodic-table/parseStringToArray'
import { getEffectiveOptionKeys } from '@/components/search/searchOptionKeys'

const props = defineProps({
  value: { type: Object, required: true },
  filters: { type: Array, default: () => [] },
})

const emit = defineEmits(['input', 'errors'])

const toNumberOrNull = (value) => (value === '' || isNil(value) ? null : Number(value))
const roundTo3 = (value) => Number(value.toFixed(3))

const getCurrentValueWithMinMaxMassNumbered = (currentValues) => {
  const minMassValue = toNumberOrNull(currentValues.minMass)
  const maxMassValue = toNumberOrNull(currentValues.maxMass)

  return { ...currentValues, minMass: minMassValue, maxMass: maxMassValue }
}

const currentValues = ref(cloneDeep(getCurrentValueWithMinMaxMassNumbered(props.value)))

const toMass = (minMass, maxMass) =>
  isNil(minMass) || isNil(maxMass) ? null : roundTo3((Number(minMass) + Number(maxMass)) / 2)

const toDeltaMassDa = (minMass, maxMass) =>
  isNil(minMass) || isNil(maxMass) ? null : roundTo3(Math.abs(Number(maxMass) - Number(minMass)) / 2)

const massDefaults = {
  isMassPlusMinusChosen: false,
  isUnitsDaltonsChosen: true,
  mass: toMass(currentValues.value.minMass, currentValues.value.maxMass),
  deltaMassDa: toDeltaMassDa(currentValues.value.minMass, currentValues.value.maxMass),
}

const massState = ref(cloneDeep(massDefaults))
const includeDialog = ref(false)
const excludeDialog = ref(false)

const fpTypes = computed(() =>
  [{ value: 'bingo', text: 'Indigo' }].concat(
    store.getters.toxprintsProfiles.map((p) => ({ value: p.name, text: p.description })),
  ),
)

const selectedFpTypeName = computed(() =>
  currentValues.value.fpType ? fpTypes.value.find((fp) => fp.value === currentValues.value.fpType)?.text : null,
)

const feature = computed(() => store.getters.feature)

const hazardCategoriesHierarchy = computed(() => store.getters.getHazardCategoriesHierarchy)

const normalizeHazardNode = (node) => ({
  ...node,
  id: node.name,
  name: node.name ?? node.id,
  description: node.description ?? '',
  children: Array.isArray(node.children) ? node.children.map(normalizeHazardNode) : [],
  hideInSelect: Boolean(node.hideInSelect),
  loading: Boolean(node.loading),
})

const hazardCategoriesTreeItems = computed(() =>
  (hazardCategoriesHierarchy.value || []).map((node) => normalizeHazardNode(node)),
)

const hetHazardSources = computed(() => store.getters.getHazardSources)
const hazardRoutes = computed(() => store.getters.getHazardRoutes)
const hazardCats = computed(() => store.getters.getHazardCats)
const hazardCodes = computed(() => store.getters.getHazardCodes)
const hazardOrganisms = computed(() => store.getters.getHazardOrganisms)

const searchTypes = computed(() => allSearchTypes.filter((t) => !t.feature || feature.value(t.feature)))

const isSimilarityDisabled = computed(() =>
  currentValues.value.searchType === 'SIMILAR' ? false : !currentValues.value.useSimilarity,
)

const isValidFormula = (formula) => {
  if (!formula) return true

  const re = /([A-Z][a-z]?\s*(\d+|[+*!]|\(\s*((\d+)|(\d+)\s*-| -\s*(\d+)|(\d+)\s*-\s*(\d+))\s*\))?)+/
  return re.test(formula)
}

const isQueryFormula = computed(
  () =>
    !isEmpty(currentValues.value.formula) &&
    (currentValues.value.formula.includes('(') ||
      currentValues.value.formula.includes('!') ||
      currentValues.value.formula.includes('+') ||
      currentValues.value.formula.includes('-')),
)

watch(isQueryFormula, (newVal) => {
  if (newVal) {
    currentValues.value.queryFormula = true
  }
})

const errorMessagesMinMass = computed(() => {
  // if (isNil(currentValues.value.minMass)) return ['no min mass']
  if (!isNil(currentValues.value.minMass)) {
    if (currentValues.value.minMass < 0) return ['min mass < 0']
    if (currentValues.value.minMass > 2000) return ['min mass > 2000']
    if (!isNil(currentValues.value.maxMass)) {
      if (currentValues.value.maxMass === currentValues.value.minMass) return ['min mass == max mass']
      if (currentValues.value.maxMass < currentValues.value.minMass) return ['min mass > max mass']
    }
  }
  return []
})

const errorMessagesMaxMass = computed(() => {
  // if (isNil(currentValues.value.maxMass)) return ['no max mass']
  if (!isNil(currentValues.value.maxMass)) {
    if (currentValues.value.maxMass < 0) return ['max mass < 0']
    if (currentValues.value.maxMass > 2000) return ['max mass > 2000']
    if (!isNil(currentValues.value.minMass)) {
      if (currentValues.value.minMass === currentValues.value.maxMass) return ['min mass == max mass']
      if (currentValues.value.minMass > currentValues.value.maxMass) return ['min mass > max mass']
    }
  }
  return []
})

const errorMessagesMass = computed(() => {
  // if (isNil(massState.value.mass)) return ['no mass']
  if (!isNil(massState.value.mass)) {
    if (massState.value.mass < 0) return ['mass < 0']
    if (massState.value.mass > 2000) return ['mass > 2000']
    if (!isNil(massState.value.deltaMassDa)) {
      if (massState.value.mass < massState.value.deltaMassDa) return ['mass < delta mass']
    }
  }
  return []
})

const errorMessagesDelta = computed(() => {
  // if (isNil(deltaMass.value)) return ['no delta mass']

  if (!isNil(deltaMass.value)) {
    if (deltaMass.value < 0) return ['delta mass < 0']
    if (massState.value.deltaMassDa > 1000) return ['delta mass > 1000 Da']
    if (!isNil(massState.value.mass)) {
      if (massState.value.mass < massState.value.deltaMassDa) return ['mass < delta mass']
    }
  }
  return []
})

const deltaMass = computed({
  get() {
    return massState.value.isUnitsDaltonsChosen
      ? massState.value.deltaMassDa
      : Math.round((massState.value.deltaMassDa * 1000000) / massState.value.mass)
  },
  set(value) {
    if (value && massState.value.mass) {
      massState.value.deltaMassDa = massState.value.isUnitsDaltonsChosen
        ? value
        : (value / 1000000) * massState.value.mass
    }
  },
})

const validateMassParams = () => {
  currentValues.value.massSearchParamsValid =
    !errorMessagesMinMass?.value?.length && !errorMessagesMaxMass?.value?.length
}

const deltaToMinMax = () => {
  if (massState.value.mass && massState.value.deltaMassDa) {
    currentValues.value.minMass = roundTo3(massState.value.mass - massState.value.deltaMassDa)
    currentValues.value.maxMass = roundTo3(massState.value.mass + massState.value.deltaMassDa)
    return
  }
  currentValues.value.minMass = null
  currentValues.value.maxMass = null
}

watch(
  () => massState.value.mass,
  () => deltaToMinMax(),
)

watch(
  () => massState.value.deltaMassDa,
  () => deltaToMinMax(),
)

watch(
  () => massState.value.isMassPlusMinusChosen,
  (newValue) => {
    if (newValue) {
      massState.value.mass = toMass(currentValues.value.minMass, currentValues.value.maxMass)
      massState.value.deltaMassDa = toDeltaMassDa(currentValues.value.minMass, currentValues.value.maxMass)
    }
  },
)

watch(
  currentValues,
  () => {
    validateMassParams()
    emit('input', currentValues.value)
  },
  { deep: true },
)

const searchInputDefaults = {
  outlined: true,
  dense: true,
  class: 'flex-1 tw-min-w-0',
}

const rowBaseClass = 'd-flex flex-row gap-2'
const rowCenterClass = `${rowBaseClass} align-items-center`
const rowBaselineClass = `${rowBaseClass} align-items-baseline`
const form = ref(null)

const areOptionsChanged = computed(
  () =>
    !isEqual(
      omit(currentValues.value, ['massSearchParamsValid', 'searchType']),
      omit(defaultSearchOptions, ['massSearchParamsValid', 'searchType']),
    ),
)

const reset = () => {
  currentValues.value = cloneDeep({ ...defaultSearchOptions, searchType: currentValues.value.searchType })
  massState.value = cloneDeep(massDefaults)
}

const formErrors = computed(() => {
  return (form.value?.inputs || [])
    .map((input) => ({
      uid: input._uid,
      hasError: input.hasError,
      label: input.label,
      messages: [
        ...new Set(
          [...(input.internalErrorMessages || []), ...(input.errorMessages || []), ...(input.errorBucket || [])].filter(
            Boolean,
          ),
        ),
      ],
    }))
    .filter((input) => input.hasError)
})

watch(formErrors, (newVal) => {
  emit('errors', newVal)
})

const hasActiveOptionKeys = (keys) => keys.every((key) => getEffectiveOptionKeys(currentValues.value).includes(key))
</script>

<template>
  <v-form ref="form">
    <div class="d-flex flex-column gap-6">
      <div class="d-flex flex-row align-items-start justify-content-between gap-2 mb-2">
        <v-btn-toggle
          v-model="currentValues.searchType"
          mandatory
          dense
          color="primary"
          class="flex-wrap gap-1"
        >
          <v-btn
            v-for="type in searchTypes"
            :key="type.value"
            :value="type.value"
          >
            <span class="text-transform-none">{{ type.text }}</span>
          </v-btn>
        </v-btn-toggle>

        <v-btn
          id="reset-button"
          aria-label="Reset search options"
          :disabled="!areOptionsChanged"
          @click="reset"
          icon
          title="Reset search options"
        >
          <!-- <v-badge
            :value="!!areOptionsChanged"
            dot
            color="primary"
          >-->
          <v-icon>mdi-filter-remove-outline</v-icon>
          <!-- </v-badge> -->
        </v-btn>
      </div>

      <!-- <v-divider class="my-0" /> -->
      <v-switch
        v-if="hasActiveOptionKeys(['useSimilarity'])"
        label="Use similarity"
        v-model="currentValues.useSimilarity"
        hide-details
      />
      <div
        v-if="hasActiveOptionKeys(['minSimilarity', 'fpType', 'similarityType'])"
        class="d-flex flex-column gap-1"
      >
        <div :class="rowBaseClass">
          <div :class="searchInputDefaults.class">
            <div>Min similarity:</div>
            <v-slider
              v-model="currentValues.minSimilarity"
              :label="String(currentValues.minSimilarity)"
              :disabled="isSimilarityDisabled"
              :step="0.01"
              :max="1"
              :min="0"
              hide-details
            />
          </div>
          <v-spacer />
        </div>

        <div :class="rowCenterClass">
          <v-select
            v-bind="searchInputDefaults"
            label="Fingerprints"
            v-model="currentValues.fpType"
            :items="fpTypes"
            :title="selectedFpTypeName"
            hide-details
          />

          <v-select
            v-bind="searchInputDefaults"
            label="Metric"
            v-model="currentValues.similarityType"
            :items="similarityTypes"
            :disabled="isSimilarityDisabled"
            hide-details
          />
        </div>
      </div>

      <div
        v-if="hasActiveOptionKeys(['formula', 'queryFormula'])"
        :class="rowBaselineClass"
      >
        <v-text-field
          v-bind="searchInputDefaults"
          label=" Molecular formula"
          v-model="currentValues.formula"
          hint="e.g. C(5-10) O5 N+"
          :rules="[(v) => isValidFormula(v) || 'Invalid molecular formula']"
          persistent-hint
        />

        <div :class="searchInputDefaults.class">
          <div class="d-flex flex-row gap-1 align-items-center">
            <v-tooltip
              top
              v-bind="defaultVTooltipProps"
            >
              <template #activator="{ on, attrs }">
                <span
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-switch
                    v-model="currentValues.queryFormula"
                    label="Query"
                    :disabled="isQueryFormula"
                    dense
                    hide-details
                  />
                </span>
              </template>

              <div class="d-flex flex-column">
                <span>Is molecular formula an exact match or a query.</span>
                <span>See more information on Molecular Formula Query Language under (i)</span>
                <!-- <span
                v-if="isEmpty(currentValues.formula)"
                class="font-weight-bold"
              >
                Query option is disabled because formula is empty.
              </span> -->
                <span
                  v-if="isQueryFormula"
                  class="font-weight-bold"
                >
                  Query option is disabled due to the presence of '(', '!', '+', or '-' in the formula.
                </span>
              </div>
            </v-tooltip>

            <v-menu
              max-width="25rem"
              max-height="35rem"
              nudge-bottom="10"
              bottom
              offset-y
              :close-on-content-click="false"
            >
              <template #activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  @click.stop
                  @mousedown.stop.prevent
                  aria-label="Show Molecular Formula Query Language"
                >
                  <v-icon small>mdi-information-outline</v-icon>
                </v-btn>
              </template>

              <MolecularFormulaManualCard />
            </v-menu>
          </div>
        </div>
      </div>

      <div
        v-if="hasActiveOptionKeys(['singleComponent', 'massType', 'minMass', 'maxMass'])"
        class="d-flex flex-column gap-2"
      >
        <v-switch
          v-model="currentValues.singleComponent"
          label="Single components"
          hide-details
        >
          <template #label>
            <div class="d-flex flex-column">
              <span>Single components</span>
              <span class="text-caption">Searches individual components in mixtures</span>
            </div>
          </template>
        </v-switch>

        <div :class="rowCenterClass">
          <v-btn-toggle
            dense
            color="primary"
            mandatory
            v-model="massState.isMassPlusMinusChosen"
            :class="searchInputDefaults.class"
          >
            <v-btn
              :value="false"
              text
            >
              <span class="text-transform-none">Min/Max</span>
            </v-btn>
            <v-btn
              :value="true"
              text
            >
              <span class="text-transform-none">±</span>
            </v-btn>
          </v-btn-toggle>

          <v-select
            v-bind="searchInputDefaults"
            v-model="currentValues.massType"
            :items="massType"
            label="Mass type"
            hide-details
          />
        </div>

        <div
          v-if="massState.isMassPlusMinusChosen"
          :class="rowBaselineClass"
        >
          <v-text-field
            v-bind="searchInputDefaults"
            label="Mass, Da"
            v-model.number="massState.mass"
            type="number"
            :min="0"
            :error-messages="errorMessagesMass"
            :max="2000"
            @input="(val) => (val === '' ? (massState.mass = null) : undefined)"
            hint="0-2000"
            persistent-hint
          />

          <span>±</span>

          <v-text-field
            v-bind="searchInputDefaults"
            label="Delta"
            v-model.number="deltaMass"
            type="number"
            :min="0"
            :error-messages="errorMessagesDelta"
            :max="2000"
            @input="(val) => (val === '' ? (deltaMass = null) : undefined)"
          >
            <template #append>
              <v-btn-toggle
                dense
                tile
                color="primary"
                mandatory
                v-model="massState.isUnitsDaltonsChosen"
              >
                <v-btn
                  :value="true"
                  small
                  text
                >
                  <span class="text-transform-none">Da</span>
                </v-btn>
                <v-btn
                  :value="false"
                  small
                  text
                >
                  <span class="text-transform-none">ppm</span>
                </v-btn>
              </v-btn-toggle>
            </template>
          </v-text-field>
        </div>
        <div
          v-else
          :class="rowCenterClass"
        >
          <v-text-field
            v-bind="searchInputDefaults"
            label="Min mass, Da"
            v-model.number="currentValues.minMass"
            type="number"
            :min="0"
            :error-messages="errorMessagesMinMass"
            :max="2000"
            @input="(val) => (val === '' ? (currentValues.minMass = null) : undefined)"
            hint="0-2000"
            persistent-hint
          />

          <v-text-field
            v-bind="searchInputDefaults"
            label="Max mass, Da"
            v-model.number="currentValues.maxMass"
            type="number"
            :min="0"
            :max="2000"
            :error-messages="errorMessagesMaxMass"
            @input="(val) => (val === '' ? (currentValues.maxMass = null) : undefined)"
            hint="0-2000"
            persistent-hint
          />
        </div>
      </div>

      <div
        class="d-flex flex-column gap-2"
        v-if="
          feature('hazard') &&
          hasActiveOptionKeys([
            'hazardNames',
            'hazardSource',
            'hazardRoute',
            'hazardCategory',
            'hazardCode',
            'hazardOrganism',
          ])
        "
      >
        <div :class="rowCenterClass">
          <div
            class="d-flex"
            :class="searchInputDefaults.class"
          >
            <NestedCategorySelect
              activator-label="Hazard Names"
              v-model="currentValues.hazardNames"
              :tree-items="hazardCategoriesTreeItems"
              expanded-state-storage-key="search-hazard-categories"
              allow-initial-empty-selection
              :v-btn-props="{
                depressed: true,
                class: `${searchInputDefaults?.class} border`,
                title: currentValues.hazardNames,
              }"
            />
          </div>

          <ClearableSelect
            v-bind="searchInputDefaults"
            label="Source"
            v-model="currentValues.hazardSource"
            :items="hetHazardSources"
            :title="currentValues.hazardSource"
            hide-details
          />
        </div>

        <div :class="rowCenterClass">
          <ClearableSelect
            v-bind="searchInputDefaults"
            v-model="currentValues.hazardRoute"
            :items="hazardRoutes"
            :title="currentValues.hazardRoute"
            label="Route"
            hide-details
          />

          <ClearableSelect
            v-bind="searchInputDefaults"
            label="Category"
            v-model="currentValues.hazardCategory"
            :items="hazardCats"
            :title="currentValues.hazardCategory"
            hide-details
          />
        </div>

        <div :class="rowCenterClass">
          <ClearableSelect
            v-bind="searchInputDefaults"
            label="Hazard Code"
            v-model="currentValues.hazardCode"
            :items="hazardCodes"
            hide-details
          />

          <ClearableSelect
            v-bind="searchInputDefaults"
            label="Test Organism"
            v-model="currentValues.hazardOrganism"
            :items="hazardOrganisms"
            :title="currentValues.hazardOrganism"
            hide-details
          />
        </div>
      </div>

      <div :class="rowBaseClass">
        <ClearableSelect
          v-bind="searchInputDefaults"
          label="Features"
          v-model="currentValues.features"
          :items="filters"
          multiple
          chips
          deletable-chips
          hide-details
        />
        <v-spacer />
      </div>

      <div :class="rowBaseClass">
        <v-text-field
          v-bind="searchInputDefaults"
          label="Included elements"
          v-model="currentValues.includeElements"
          :disabled="!hasActiveOptionKeys(['includeElements'])"
          title="Elements that must be included in the search"
          hint="e.g. C,F,H"
          :rules="[!hasInvalidElementSymbol(currentValues.includeElements) || 'Invalid element symbol']"
          persistent-hint
        >
          <template #append>
            <v-dialog
              v-model="includeDialog"
              max-width="50rem"
            >
              <template #activator="{ on, attrs }">
                <v-btn
                  icon
                  color="primary"
                  v-bind="attrs"
                  v-on="on"
                  :disabled="!hasActiveOptionKeys(['includeElements'])"
                >
                  <v-icon>mdi-periodic-table</v-icon>
                </v-btn>
              </template>

              <v-card>
                <v-card-title class="d-flex flex-row align-items-center justify-content-between">
                  <span>Elements must be included</span>
                  <v-btn
                    icon
                    @click="includeDialog = false"
                  >
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </v-card-title>
                <v-card-text>
                  <PeriodicTable
                    v-model="currentValues.includeElements"
                    :excluded-elements="currentValues.excludeElements"
                  />
                  <div class="d-flex flex-row w-100 justify-content-end">
                    <v-btn
                      text
                      @click="includeDialog = false"
                    >
                      Ok
                    </v-btn>
                  </div>
                </v-card-text>
              </v-card>
            </v-dialog>
          </template>
        </v-text-field>

        <v-text-field
          v-bind="searchInputDefaults"
          label="Excluded elements"
          :disabled="!hasActiveOptionKeys(['excludeElements'])"
          v-model="currentValues.excludeElements"
          title="Elements that must be excluded from the search"
          hint="e.g. C,F,H"
          :rules="[
            !hasInvalidElementSymbol(currentValues.excludeElements) || 'Invalid element symbol',
            !isStringOverlapping(currentValues.includeElements, currentValues.excludeElements) ||
              'An element cannot be both included and excluded',
          ]"
          persistent-hint
        >
          <template #append>
            <v-dialog
              v-model="excludeDialog"
              max-width="50rem"
            >
              <template #activator="{ on, attrs }">
                <v-btn
                  icon
                  color="primary"
                  v-bind="attrs"
                  v-on="on"
                  :disabled="!hasActiveOptionKeys(['excludeElements'])"
                >
                  <v-icon>mdi-periodic-table</v-icon>
                </v-btn>
              </template>

              <v-card>
                <v-card-title class="d-flex flex-row align-items-center justify-content-between">
                  <span>Elements must be excluded</span>
                  <v-btn
                    icon
                    @click="excludeDialog = false"
                  >
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </v-card-title>
                <v-card-text>
                  <PeriodicTable
                    v-model="currentValues.excludeElements"
                    :excluded-elements="currentValues.includeElements"
                  />
                  <div class="d-flex flex-row w-100 justify-content-end">
                    <v-btn
                      text
                      @click="excludeDialog = false"
                    >
                      Ok
                    </v-btn>
                  </div>
                </v-card-text>
              </v-card>
            </v-dialog>
          </template>
        </v-text-field>
      </div>

      <div :class="rowBaseClass">
        <ClearableSelect
          v-bind="searchInputDefaults"
          label="Minimum toxicity"
          v-model="currentValues.toxicity"
          :items="toxicities"
          hide-details
        />

        <ClearableSelect
          v-bind="searchInputDefaults"
          label="Minimum authority"
          v-model="currentValues.authority"
          :items="authorities"
          hide-details
        />
      </div>

      <div :class="rowBaseClass">
        <v-text-field
          v-bind="searchInputDefaults"
          label="Min. No. of Structures"
          v-model.number="currentValues.minNumOfStructures"
          type="number"
          :min="1"
          title="Minimum number of structures to be returned by the similarity search"
          @input="
            (val) =>
              val === ''
                ? (currentValues.minNumOfStructures = null)
                : val < 1
                  ? (currentValues.minNumOfStructures = Math.abs(val))
                  : undefined
          "
          hide-details
        />

        <v-text-field
          v-bind="searchInputDefaults"
          label="Max. No. of Structures"
          v-model.number="currentValues.maxNumOfStructures"
          type="number"
          :min="1"
          title="Maximum number of structures to return"
          @input="
            (val) =>
              val === ''
                ? (currentValues.maxNumOfStructures = null)
                : val < 1
                  ? (currentValues.maxNumOfStructures = Math.abs(val))
                  : undefined
          "
          hide-details
        />
      </div>
    </div>
  </v-form>
</template>

<style scoped>
.text-transform-none {
  text-transform: none !important;
}

.v-input--selection-controls {
  margin-top: 0;
  padding-top: 0;
}
</style>
